import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Button, Card, Typography } from 'seagull';
import ChangesApprovalInfoBox from '~/components/ChangesApprovalInfoBox/ChangesApprovalInfoBox';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { useApproveAmendments } from '~/hooks/useApproveAmendments';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { Intent } from '~/infra/access-control';
import { handleRequest } from '~/infra/utils';
import { timeModel } from '~/models';
import styles from './ChangesSummary.module.scss';
import { getChangesSummary } from './ChangesSummary.utils';
export default function ChangesSummary(props) {
    const { relationship, testId, isVendor, refresh } = props;
    const { SERVICE_TEMPLATE_BUNDLE = false } = useFeatureFlags();
    const { api, logBI } = useStoreActions();
    const overlayContext = useOverlayContext();
    const approveAmendmentsRequest = useApproveAmendments();
    const [isApproving, setIsApproving] = useState(false);
    const [isDisabling, setIsDisabling] = useState(false);
    const changesSummary = useMemo(()=>{
        return getChangesSummary(relationship, SERVICE_TEMPLATE_BUNDLE);
    }, [
        relationship,
        SERVICE_TEMPLATE_BUNDLE
    ]);
    async function approveAmendments() {
        logBI(10150);
        setIsApproving(true);
        await approveAmendmentsRequest(_object_spread_props(_object_spread({}, relationship), {
            onSuccess: ()=>refresh({
                    silent: true
                })
        }));
        setIsApproving(false);
    }
    async function disableAutoApproval() {
        logBI(10151);
        setIsDisabling(true);
        const { success } = await handleRequest(api.relationship.stopAutoApproveAmendments(relationship.id));
        if (!success) {
            overlayContext.showErrorToast("Failed to disable auto-approval, please try again", "disable-auto-approval-error");
        } else {
            await refresh({
                silent: true
            });
            overlayContext.showSuccessToast("Disabled auto-approval", "disable-auto-approval-success");
        }
        setIsDisabling(false);
    }
    return changesSummary.length > 0 ? /*#__PURE__*/ _jsx(Card, {
        disableSeparator: true,
        skin: "info",
        title: /*#__PURE__*/ _jsx(Typography, {
            variant: "h6",
            weight: "normal",
            children: "Changes awaiting approval"
        }),
        padding: "m",
        testId: testId,
        actions: !isVendor && /*#__PURE__*/ _jsx(Button, {
            intent: Intent.ApproveAllAmendments,
            size: "small",
            testId: "".concat(testId, "-approve"),
            loading: isApproving,
            tooltip: {
                placement: 'top',
                title: 'Approves all agreement amendments'
            },
            onClick: approveAmendments,
            bi: {
                name: 'approve_all_amendments'
            },
            children: "Approve"
        }),
        children: changesSummary.map((param, i)=>{
            let { autoApprovalDate, changes, amendmentDate } = param;
            return /*#__PURE__*/ _jsxs("div", {
                children: [
                    amendmentDate && /*#__PURE__*/ _jsxs(Typography, {
                        className: styles.amendmentDate,
                        testId: "".concat(testId, "-").concat(i, "-made-on"),
                        children: [
                            "Made on ",
                            timeModel.formatDate(amendmentDate)
                        ]
                    }),
                    /*#__PURE__*/ _jsx("ul", {
                        className: styles.changes,
                        children: changes.map((text, idx)=>/*#__PURE__*/ _jsx(Typography, {
                                as: "li",
                                variant: "paragraph2",
                                className: styles.changeItem,
                                testId: "".concat(testId, "-").concat(i, "-").concat(idx),
                                children: text
                            }, text))
                    }),
                    autoApprovalDate && /*#__PURE__*/ _jsx(ChangesApprovalInfoBox, {
                        testId: "".concat(testId, "-").concat(i, "-approval-info"),
                        className: styles.approvalInfo,
                        onDisable: disableAutoApproval,
                        disabling: isDisabling,
                        autoApprovalDate: autoApprovalDate,
                        showDisableButton: !isVendor
                    })
                ]
            }, i);
        })
    }) : null;
}
